import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { HistoryEntry } from '@/types/fitness';
import { cachedQuery, removeCachedData } from '@/lib/offlineStorage';

function formatIsoDateToDisplay(isoDate: string) {
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) return isoDate;
  return `${day}/${month}/${year}`;
}

function toComparableDate(fecha: string) {
  const [day, month, year] = fecha.split('/').map(Number);
  if (!day || !month || !year) return 0;
  return new Date(year, month - 1, day).getTime();
}

function valueOrDash(value: number | undefined) {
  return value ?? null;
}

function buildEntrySignature(entry: HistoryEntry) {
  return [
    entry.fecha,
    entry.peso,
    entry.grasa,
    valueOrDash(entry.imc),
    valueOrDash(entry.masaLibreGrasa),
    valueOrDash(entry.agua),
    valueOrDash(entry.grasaVisceral),
    valueOrDash(entry.metabolismoBasal),
    valueOrDash(entry.masaMuscular),
  ].join('|');
}

/**
 * Fetches body_measurements for the active user by user_id only.
 * Merges with static historial and deduplicates by metric signature.
 * Uses cached data when offline.
 */
export function useBodyMeasurements(staticHistorial: HistoryEntry[], activeUser: string, overrideUserId?: string | null) {
  const [mergedHistorial, setMergedHistorial] = useState<HistoryEntry[]>(staticHistorial);
  const [loading, setLoading] = useState(false);

  const fetchMeasurements = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setMergedHistorial(staticHistorial);
        return;
      }

      const targetUserId = overrideUserId || user.id;

      // Invalidate all measurement caches so UI reflects new data
      removeCachedData(`body_measurements_${targetUserId}`);
      removeCachedData(`measurements_${targetUserId}`);

      const { data } = await cachedQuery(`body_measurements_${targetUserId}`, () =>
        supabase
          .from('body_measurements')
          .select('*')
          .eq('user_id', targetUserId)
          .order('measure_date', { ascending: true })
          .order('created_at', { ascending: true })
      );

      if (!data) {
        setMergedHistorial(staticHistorial);
        return;
      }

      const dbEntries: HistoryEntry[] = (data as any[]).map((row) => ({
        sesion: row.session_label,
        fecha: formatIsoDateToDisplay(row.measure_date),
        peso: Number(row.peso) || 0,
        grasa: Number(row.grasa) || 0,
        imc: row.imc != null ? Number(row.imc) : undefined,
        masaLibreGrasa: row.masa_libre_grasa != null ? Number(row.masa_libre_grasa) : undefined,
        agua: row.agua != null ? Number(row.agua) : undefined,
        grasaVisceral: row.grasa_visceral != null ? Number(row.grasa_visceral) : undefined,
        metabolismoBasal: row.metabolismo_basal != null ? Number(row.metabolismo_basal) : undefined,
        masaMuscular: row.masa_muscular != null ? Number(row.masa_muscular) : undefined,
      }));

      const signatures = new Set<string>();
      const deduped = [...staticHistorial, ...dbEntries].filter((entry) => {
        const signature = buildEntrySignature(entry);
        if (signatures.has(signature)) return false;
        signatures.add(signature);
        return true;
      });

      deduped.sort((a, b) => toComparableDate(a.fecha) - toComparableDate(b.fecha));
      setMergedHistorial(deduped);
    } catch {
      setMergedHistorial(staticHistorial);
    } finally {
      setLoading(false);
    }
  }, [activeUser, staticHistorial, overrideUserId]);

  useEffect(() => {
    fetchMeasurements();
  }, [fetchMeasurements]);

  return { historial: mergedHistorial, loading, refetch: fetchMeasurements };
}
